import type { PersistStorage, StorageValue } from "zustand/middleware";
import { recordDiagnostic } from "./diagnosticsLog";
import { BACKUP_ARCHIVE_KEY, compactLocalBackups, type StorageLike } from "./backupArchive";
import {
  careerDbApi,
  careerStorageNotice,
  setCareerStorageNotice,
  type CareerDbApi,
  type CareerDbFile,
  type CareerDbStatus,
} from "./careerDbClient";
import { readPreferRustCareerFlush, serializeCareerPersistValue } from "./careerFlush";
import { hydrateFlightLogs, projectLogsFromMissions } from "./flightlog";
import { PERSIST_DEBOUNCE_MS } from "./mapMemory";
import type { Mission } from "./types";
import { FUEL_LB_BACKUP_KEY, repairStoredFuelPounds } from "./wholeFuel";

/** Zustand persist key for the career desk. */
export const DISPATCH_STORE_KEY = "ns.dispatch.store";

/** Tiny theme stub so first paint still works after the fat store leaves localStorage. */
export const DESK_CHROME_KEY = "ns.dispatch.chrome";

/**
 * Stay well under the WebView localStorage cliff (~5MB for the whole origin).
 * Oversized careers go to IndexedDB; localStorage keeps chrome + capped backups.
 */
export const LOCAL_STORE_SOFT_MAX_CHARS = 1_200_000;

export type PersistKv = {
  getItem(name: string): string | null | Promise<string | null>;
  setItem(name: string, value: string): unknown | Promise<unknown>;
  removeItem(name: string): unknown | Promise<unknown>;
};

export type RecoverPersistResult = {
  value: string;
  recovered: boolean;
  prunedLogs: number;
};

const memoryFallback: { value: string | null } = { value: null };

export function createMemoryKv(seed: string | null = null): PersistKv {
  let value = seed;
  return {
    getItem: () => value,
    setItem: (_name, next) => {
      value = next;
    },
    removeItem: () => {
      value = null;
    },
  };
}

export function isQuotaExceededError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: number; message?: string };
  if (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (e.code === 22 || e.code === 1014) return true;
  return typeof e.message === "string" && /exceeded the quota|quota exceeded|QUOTA_EXCEEDED/i.test(e.message);
}

export function persistStateFromRaw(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const rec = parsed as { state?: unknown };
    if (rec.state && typeof rec.state === "object") return rec.state as Record<string, unknown>;
    return rec as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function recoverOversizedPersistState(state: Record<string, unknown>): {
  state: Record<string, unknown>;
  prunedLogs: number;
} {
  const missions = Array.isArray(state.missions) ? (state.missions as Mission[]) : [];
  const rawLogs = Array.isArray(state.flightLogs) ? state.flightLogs : [];
  const nextLogs = projectLogsFromMissions(hydrateFlightLogs(rawLogs), missions);
  return {
    state: { ...state, flightLogs: nextLogs },
    prunedLogs: Math.max(0, rawLogs.length - nextLogs.length),
  };
}

/** Drop never-flown junk Flight Log rows / ghost filed orphans from a persist blob. */
export function recoverPersistBlob(raw: string): RecoverPersistResult {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return { value: raw, recovered: false, prunedLogs: 0 };
    }
    const rec = parsed as { state?: unknown; version?: unknown };
    const inner =
      rec.state && typeof rec.state === "object" ? (rec.state as Record<string, unknown>) : (rec as Record<string, unknown>);
    const { state, prunedLogs } = recoverOversizedPersistState(inner);
    const next = rec.state && typeof rec.state === "object" ? { ...rec, state } : state;
    return {
      value: JSON.stringify(next),
      recovered: prunedLogs > 0,
      prunedLogs,
    };
  } catch {
    return { value: raw, recovered: false, prunedLogs: 0 };
  }
}

export function chromeFromPersistState(state: Record<string, unknown> | null | undefined): {
  theme?: unknown;
  themePack?: unknown;
  themeAccent?: unknown;
} | null {
  if (!state) return null;
  const company = state.company && typeof state.company === "object" ? (state.company as Record<string, unknown>) : null;
  return {
    theme: state.theme ?? company?.theme,
    themePack: state.themePack ?? company?.themePack,
    themeAccent: state.themeAccent ?? company?.themeAccent,
  };
}

export function writeDeskChromeStub(storage: PersistKv | StorageLike | null | undefined, persistValue: string): void {
  if (!storage) return;
  const chrome = chromeFromPersistState(persistStateFromRaw(persistValue));
  if (!chrome) return;
  try {
    storage.setItem(DESK_CHROME_KEY, JSON.stringify(chrome));
  } catch {
    /* chrome stub is best-effort */
  }
}

export function readDeskChromePersist(storage: Pick<Storage, "getItem"> | null | undefined): {
  theme?: unknown;
  themePack?: unknown;
  themeAccent?: unknown;
} | null {
  if (!storage) return null;
  const stub = storage.getItem(DESK_CHROME_KEY);
  if (stub) {
    try {
      const parsed = JSON.parse(stub) as unknown;
      if (parsed && typeof parsed === "object") return parsed as { theme?: unknown; themePack?: unknown; themeAccent?: unknown };
    } catch {
      /* fall through to the persist blob */
    }
  }
  const raw = storage.getItem(DISPATCH_STORE_KEY);
  if (!raw) return null;
  return chromeFromPersistState(persistStateFromRaw(raw));
}

function browserLocalOrMemory(): PersistKv {
  if (typeof localStorage !== "undefined") return localStorage;
  return {
    getItem: () => memoryFallback.value,
    setItem: (_key, value) => {
      memoryFallback.value = value;
    },
    removeItem: () => {
      memoryFallback.value = null;
    },
  };
}

async function settled<T>(value: T | Promise<T>): Promise<T> {
  return value;
}

function createIndexedDbKv(): PersistKv | null {
  if (typeof indexedDB === "undefined") return null;
  let dbPromise: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      let req: IDBOpenDBRequest;
      try {
        req = indexedDB.open("ns.dispatch.persist", 1);
      } catch (err) {
        dbPromise = null;
        reject(err);
        return;
      }
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbPromise = null;
        reject(req.error ?? new Error("indexedDB open failed"));
      };
    });
    return dbPromise;
  };

  return {
    async getItem(name) {
      const db = await open();
      return new Promise<string | null>((resolve, reject) => {
        const tx = db.transaction("kv", "readonly");
        const req = tx.objectStore("kv").get(name);
        req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : null);
        req.onerror = () => reject(req.error ?? new Error("indexedDB get failed"));
      });
    },
    async setItem(name, value) {
      const db = await open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction("kv", "readwrite");
        const req = tx.objectStore("kv").put(value, name);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error("indexedDB put failed"));
      });
    },
    async removeItem(name) {
      const db = await open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction("kv", "readwrite");
        const req = tx.objectStore("kv").delete(name);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error("indexedDB delete failed"));
      });
    },
  };
}

export type DeskPersistOptions = {
  local?: PersistKv | null;
  durable?: PersistKv | null;
  recover?: (raw: string) => RecoverPersistResult;
  compactBackups?: (local: PersistKv) => void;
  /** Coalesce career writes. 0 (default) writes immediately — tests stay sync. */
  debounceMs?: number;
  /**
   * Tauri SQLite career store. Undefined uses the API registered at desk
   * startup. Null keeps the WebView store. Tests pass a fake.
   */
  careerDb?: CareerDbApi | null;
};

export { PERSIST_DEBOUNCE_MS };

function defaultCompactBackups(local: PersistKv): void {
  compactLocalBackups(local);
}

async function readKv(kv: PersistKv | null | undefined, name: string): Promise<string | null> {
  if (!kv) return null;
  const raw = await settled(kv.getItem(name));
  return typeof raw === "string" ? raw : null;
}

/**
 * Career persist that does not blow WebView localStorage.
 * IndexedDB (or an injected durable KV — Tauri FS equivalent) is primary;
 * localStorage is a small fallback / migration source. setItem never throws.
 */
export function createDeskPersistStorage(opts: DeskPersistOptions = {}): PersistKv {
  const local = opts.local === undefined ? browserLocalOrMemory() : opts.local;
  const durable = opts.durable === undefined ? createIndexedDbKv() : opts.durable;
  const recover = opts.recover ?? recoverPersistBlob;
  const compactBackups = opts.compactBackups ?? (local ? defaultCompactBackups : undefined);
  const debounceMs = Math.max(0, opts.debounceMs ?? 0);
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingWrite: { name: string; value: string } | null = null;
  const persistWaiters: Array<() => void> = [];

  if (local) {
    try {
      compactBackups?.(local);
    } catch {
      /* leftover backups must never block hydrate */
    }
  }

  const finishHydrate = (fuel: string): string => {
    const recovered = recover(fuel);
    return recovered.recovered ? recovered.value : fuel;
  };

  type CareerMode = "sqlite" | "read_only" | "legacy";
  let modeCache: CareerMode | null = null;
  let modeWait: Promise<CareerMode> | null = null;

  const resolveCareerDb = (): CareerDbApi | null => {
    if (opts.careerDb !== undefined) return opts.careerDb;
    return careerDbApi();
  };

  const noteCareer = (message: string | null): void => {
    const prev = careerStorageNotice();
    setCareerStorageNotice(message);
    if (message && message !== prev) {
      recordDiagnostic({
        module: "persist",
        severity: "error",
        message,
      });
    }
  };

  const errorText = (err: unknown, fallback: string): string => {
    if (typeof err === "string" && err.trim()) return err.trim();
    if (err instanceof Error && err.message.trim()) return err.message;
    return fallback;
  };

  const readQuiet = async (kv: PersistKv | null | undefined, name: string): Promise<string | null> => {
    try {
      return await readKv(kv, name);
    } catch {
      return null;
    }
  };

  const parsesJson = (text: string): boolean => {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  const collectCareerFiles = async (): Promise<{ files: CareerDbFile[]; unreadable: boolean }> => {
    let durableFailed = false;
    let durableStore: string | null = null;
    try {
      durableStore = await readKv(durable, DISPATCH_STORE_KEY);
    } catch {
      durableFailed = true;
    }
    const localStore = await readQuiet(local, DISPATCH_STORE_KEY);
    if (durableFailed && !localStore) return { files: [], unreadable: true };
    const files: CareerDbFile[] = [];
    const primary = durableStore && durableStore.length > 0 ? durableStore : localStore;
    if (primary && primary.length > 0) {
      files.push({ name: "ns.dispatch.store.json", text: primary });
    }
    if (durableStore && localStore && durableStore !== localStore && parsesJson(localStore)) {
      files.push({ name: "ns.dispatch.store.local.json", text: localStore });
    }
    const extras: Array<[string, string]> = [
      [DESK_CHROME_KEY, "ns.dispatch.chrome.json"],
      [FUEL_LB_BACKUP_KEY, "ns.dispatch.store.fuel-lb-backup.json"],
      [BACKUP_ARCHIVE_KEY, "ns.dispatch.backups.json"],
      ["ns.dispatch.career.cache", "ns.dispatch.career.cache.json"],
    ];
    for (const [key, name] of extras) {
      const fromDurable = await readQuiet(durable, key);
      const fromLocal = await readQuiet(local, key);
      const text =
        fromDurable && parsesJson(fromDurable) ? fromDurable : fromLocal && parsesJson(fromLocal) ? fromLocal : null;
      if (text) files.push({ name, text });
    }
    return { files, unreadable: false };
  };

  const computeMode = async (api: CareerDbApi): Promise<CareerMode> => {
    let status: CareerDbStatus;
    try {
      status = await api.status();
    } catch (err) {
      noteCareer(errorText(err, "Career database is unavailable. The desk is still on the previous store."));
      return "legacy";
    }
    if (status.mode === "sqlite") {
      noteCareer(null);
      return "sqlite";
    }
    if (status.mode === "read_only") {
      noteCareer(status.message || "Career database is read-only. The desk did not overwrite it.");
      return "read_only";
    }
    if (status.mode === "legacy") {
      if (status.message) noteCareer(status.message);
      return "legacy";
    }
    const collected = await collectCareerFiles();
    if (collected.unreadable) {
      noteCareer("Career files could not be read. The desk is still on the previous store.");
      return "legacy";
    }
    let migrated: CareerDbStatus;
    try {
      migrated = await api.migrate(collected.files);
    } catch (err) {
      noteCareer(errorText(err, "Career database was not switched. The desk is still on the previous store."));
      return "legacy";
    }
    if (migrated.mode === "sqlite") {
      noteCareer(null);
      return "sqlite";
    }
    if (migrated.mode === "read_only") {
      noteCareer(migrated.message || "Career database is read-only. The desk did not overwrite it.");
      return "read_only";
    }
    if (migrated.message) noteCareer(migrated.message);
    return "legacy";
  };

  const ensureMode = (api: CareerDbApi): Promise<CareerMode> => {
    if (modeCache) return Promise.resolve(modeCache);
    if (!modeWait) {
      modeWait = computeMode(api)
        .then((mode) => {
          modeCache = mode;
          return mode;
        })
        .catch((err: unknown) => {
          noteCareer(errorText(err, "Career database was not switched. The desk is still on the previous store."));
          modeCache = "legacy";
          return "legacy" as CareerMode;
        });
    }
    return modeWait;
  };

  const sqliteKv = (api: CareerDbApi): PersistKv => ({
    getItem: (name) => api.load(name),
    setItem: (name, value) => api.save(name, value),
    removeItem: () => undefined,
  });

  const displayRaw = (raw: string | null): string | null => {
    if (raw == null) return null;
    const repaired = repairStoredFuelPounds(raw);
    return finishHydrate(repaired.changed ? repaired.value : raw);
  };

  const readThroughCareerDb = async (name: string): Promise<{ routed: true; value: string | null } | { routed: false }> => {
    const api = resolveCareerDb();
    if (!api) return { routed: false };
    const mode = await ensureMode(api);
    if (mode === "legacy") return { routed: false };
    if (mode === "read_only") {
      try {
        const loaded = await api.load(name);
        if (loaded != null) return { routed: true, value: displayRaw(loaded) };
      } catch (err) {
        noteCareer(errorText(err, "Career database could not be read. The desk is read-only."));
      }
      const rawDurable = await readQuiet(durable, name);
      if (rawDurable != null) return { routed: true, value: displayRaw(rawDurable) };
      return { routed: true, value: displayRaw(await readQuiet(local, name)) };
    }
    try {
      const loaded = await api.load(name);
      if (loaded == null) return { routed: true, value: null };
      const hydrated = hydrateValue(sqliteKv(api), loaded);
      return { routed: true, value: hydrated instanceof Promise ? await hydrated : hydrated };
    } catch (err) {
      noteCareer(errorText(err, "Career database could not be read."));
      return { routed: true, value: null };
    }
  };

  const writeThroughCareerDb = async (name: string, value: string): Promise<boolean> => {
    const api = resolveCareerDb();
    if (!api) return false;
    const mode = await ensureMode(api);
    if (mode === "legacy") return false;
    if (mode === "read_only") {
      noteCareer(careerStorageNotice() || "Career database is read-only. This save was not written.");
      return true;
    }
    let next = value;
    const first = recover(value);
    if (first.recovered) next = first.value;
    try {
      await api.save(name, next);
      if (name === DISPATCH_STORE_KEY && local) writeDeskChromeStub(local, next);
    } catch (err) {
      noteCareer(errorText(err, "Career database could not be updated. The previous copy was kept."));
    }
    return true;
  };

  const keepCareerDbOnRemove = async (): Promise<boolean> => {
    const api = resolveCareerDb();
    if (!api) return false;
    const mode = await ensureMode(api);
    return mode === "sqlite" || mode === "read_only";
  };

  // Whole pounds stay on the same turn as the KV read. Awaiting a backup here
  // lets desk startup land after a test (or a screen) has already written state.
  const hydrateValue = (store: PersistKv | null, raw: string | null): string | Promise<string> | null => {
    if (raw == null) return null;
    const repaired = repairStoredFuelPounds(raw);
    if (!repaired.changed) return finishHydrate(raw);
    return (async () => {
      if (!store) return finishHydrate(raw);
      try {
        const existing = await readKv(store, FUEL_LB_BACKUP_KEY);
        if (existing == null || existing.length === 0) {
          await settled(store.setItem(FUEL_LB_BACKUP_KEY, raw));
          const check = await readKv(store, FUEL_LB_BACKUP_KEY);
          if (check == null || check.length === 0) return finishHydrate(raw);
        }
      } catch {
        return finishHydrate(raw);
      }
      return finishHydrate(repaired.value);
    })();
  };

  const inner: PersistKv = {
    getItem(name) {
      return Promise.resolve()
        .then(async () => {
          const careerRead = await readThroughCareerDb(name);
          if (careerRead.routed) return careerRead.value;
          try {
            const rawDurable = await readKv(durable, name);
            const hydrated = hydrateValue(durable, rawDurable);
            const fromDurable = hydrated instanceof Promise ? await hydrated : hydrated;
            if (fromDurable != null) return fromDurable;
          } catch {
            /* try local */
          }
          try {
            const rawLocal = await readKv(local, name);
            const hydrated = hydrateValue(local, rawLocal);
            return hydrated instanceof Promise ? await hydrated : hydrated;
          } catch (err) {
            if (isQuotaExceededError(err) && local) {
              try {
                compactBackups?.(local);
              } catch {
                /* still fail soft */
              }
            }
            return null;
          }
        })
        .catch(() => null);
    },
    setItem(name, value) {
      return Promise.resolve()
        .then(async () => {
          if (await writeThroughCareerDb(name, value)) return;
          let next = value;
          const first = recover(value);
          if (first.recovered) next = first.value;

          const writeDurable = async (payload: string): Promise<boolean> => {
            if (!durable) return false;
            try {
              await settled(durable.setItem(name, payload));
              return true;
            } catch (err) {
              if (!isQuotaExceededError(err) || !local) return false;
              try {
                compactBackups?.(local);
              } catch {
                /* continue */
              }
              const again = recover(payload);
              next = again.value;
              try {
                await settled(durable.setItem(name, next));
                return true;
              } catch {
                return false;
              }
            }
          };

          const writeLocal = async (payload: string): Promise<void> => {
            if (!local) return;
            const offload = async () => {
              writeDeskChromeStub(local, payload);
              try {
                await settled(local.removeItem(name));
              } catch {
                /* ignore */
              }
            };
            if (payload.length > LOCAL_STORE_SOFT_MAX_CHARS) {
              await offload();
              return;
            }
            try {
              await settled(local.setItem(name, payload));
              writeDeskChromeStub(local, payload);
            } catch (err) {
              if (local) {
                try {
                  compactBackups?.(local);
                } catch {
                  /* continue */
                }
              }
              const again = recover(payload);
              next = again.value;
              if (next.length > LOCAL_STORE_SOFT_MAX_CHARS) {
                await offload();
                return;
              }
              try {
                await settled(local.setItem(name, next));
                writeDeskChromeStub(local, next);
              } catch {
                writeDeskChromeStub(local, next);
                try {
                  await settled(local.removeItem(name));
                } catch {
                  /* desk stays up — durable copy is the source of truth */
                }
              }
              if (!isQuotaExceededError(err)) {
                /* still fail soft — never rethrow into the desk tree */
              }
            }
          };

          await writeDurable(next);
          await writeLocal(next);
        })
        .catch((err) => {
          console.warn("ns.dispatch.store persist failed softly", err);
          recordDiagnostic({
            module: "persist",
            severity: "error",
            message: err instanceof Error ? err.message : "Desk save failed.",
            stack: err instanceof Error ? err.stack : undefined,
          });
        });
    },
    removeItem(name) {
      return Promise.resolve()
        .then(async () => {
          if (await keepCareerDbOnRemove()) return;
          if (durable) {
            try {
              await settled(durable.removeItem(name));
            } catch {
              /* ignore */
            }
          }
          if (local) {
            try {
              await settled(local.removeItem(name));
            } catch {
              /* ignore */
            }
          }
        })
        .catch(() => undefined);
    },
  };

  if (debounceMs <= 0) return inner;

  const flushPersist = (): Promise<void> => {
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    const job = pendingWrite;
    pendingWrite = null;
    const waiters = persistWaiters.splice(0);
    if (!job) {
      waiters.forEach((fn) => fn());
      return Promise.resolve();
    }
    return Promise.resolve(inner.setItem(job.name, job.value)).then(
      () => {
        waiters.forEach((fn) => fn());
      },
      () => {
        waiters.forEach((fn) => fn());
      },
    );
  };

  if (typeof document !== "undefined") {
    const onHide = () => {
      void flushPersist();
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    globalThis.addEventListener?.("pagehide", onHide);
  }

  return {
    getItem: inner.getItem.bind(inner),
    setItem(name, value) {
      pendingWrite = { name, value };
      return new Promise<void>((resolve) => {
        persistWaiters.push(resolve);
        if (persistTimer) return;
        persistTimer = setTimeout(() => {
          void flushPersist();
        }, debounceMs);
      });
    },
    removeItem(name) {
      return flushPersist().then(() => inner.removeItem(name));
    },
  };
}

type InputPending = (options?: { includeContinuous?: boolean }) => boolean;

function inputIsPending(): boolean {
  const scheduling = (globalThis.navigator as { scheduling?: { isInputPending?: InputPending } } | undefined)
    ?.scheduling;
  try {
    return Boolean(scheduling?.isInputPending?.({ includeContinuous: true }));
  } catch {
    return false;
  }
}

/**
 * Career stringify is synchronous and large. If a click or key is already
 * queued, wait a frame so the desk handles it before the blob is serialized.
 */
export async function waitForIdleInput(opts?: {
  isInputPending?: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  maxWaits?: number;
}): Promise<void> {
  const pending = opts?.isInputPending ?? inputIsPending;
  const sleep =
    opts?.sleep ??
    ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const max = opts?.maxWaits ?? 8;
  for (let i = 0; i < max; i += 1) {
    if (!pending()) return;
    await sleep(32);
  }
}

/**
 * Zustand persist storage that coalesces JSON.stringify of the career blob.
 * 0.3.69 only delayed the IDB/local write — stringify still ran on every store tick
 * and froze the WebView during scroll / account switch.
 *
 * When the blob's Desk Ops flag prefers Rust, the debounced stringify goes
 * through serializeCareerPersistValue (Tauri command, TypeScript fallback).
 * The flag defaults off, so this path stays JSON.stringify.
 * Either path waits out pending input first so a click is not stuck behind the blob.
 */
export function createDeskPersistJsonStorage<T>(opts: DeskPersistOptions = {}): PersistStorage<T> {
  const debounceMs = Math.max(0, opts.debounceMs ?? 0);
  const kv = createDeskPersistStorage({ ...opts, debounceMs: 0 });
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { name: string; value: StorageValue<T> } | null = null;
  const waiters: Array<() => void> = [];

  const commit = (name: string, value: unknown): Promise<void> =>
    waitForIdleInput().then(() =>
      serializeCareerPersistValue(value).then((out) => {
        if (!out.ok) return undefined;
        return Promise.resolve(kv.setItem(name, out.json)).then(() => undefined);
      }),
    );

  const flush = (): Promise<void> => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const job = pending;
    pending = null;
    const resolveAll = waiters.splice(0);
    if (!job) {
      resolveAll.forEach((fn) => fn());
      return Promise.resolve();
    }
    return commit(job.name, job.value).then(
      () => {
        resolveAll.forEach((fn) => fn());
      },
      () => {
        resolveAll.forEach((fn) => fn());
      },
    );
  };

  if (debounceMs > 0 && typeof document !== "undefined") {
    const onHide = () => {
      void flush();
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    globalThis.addEventListener?.("pagehide", onHide);
  }

  return {
    getItem: async (name) => {
      const raw = await kv.getItem(name);
      if (raw == null) return null;
      try {
        return JSON.parse(raw) as StorageValue<T>;
      } catch {
        return null;
      }
    },
    setItem(name, value) {
      if (debounceMs <= 0 && !readPreferRustCareerFlush(value)) {
        try {
          return kv.setItem(name, JSON.stringify(value));
        } catch (err) {
          console.warn("ns.dispatch.store persist stringify failed softly", err);
          return undefined;
        }
      }
      if (debounceMs <= 0) {
        return commit(name, value);
      }
      pending = { name, value };
      return new Promise<void>((resolve) => {
        waiters.push(resolve);
        if (timer) return;
        timer = setTimeout(() => {
          void flush();
        }, debounceMs);
      });
    },
    removeItem(name) {
      return flush().then(() => kv.removeItem(name));
    },
  };
}
